import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { elsterApi, type ElsterReadinessIssue } from '../../../lib/api';

/**
 * Proaktiver Hinweis-Banner: prueft beim Oeffnen einer Steuerart, ob alle
 * ELSTER-Voraussetzungen erfuellt sind (Steuernummer, Adresse, Zertifikat,
 * Lizenz, Tarif). Fehlt etwas, wird es klar benannt — mit direktem Link zur
 * passenden Einstellungsseite, statt den Nutzer in einen ERiC-Fehler laufen
 * zu lassen.
 *
 * Optionales `onReady`-Callback meldet den Status nach oben (z.B. um den
 * Uebermitteln-Button zu (de)aktivieren).
 */
export function ReadinessBanner({
  companyId,
  onReady,
}: {
  companyId: string;
  onReady?: (ready: boolean) => void;
}): ReactElement | null {
  const [issues, setIssues] = useState<ElsterReadinessIssue[] | null>(null);

  useEffect(() => {
    let alive = true;
    elsterApi
      .readiness(companyId)
      .then((r) => {
        if (!alive) return;
        setIssues(r.issues);
        onReady?.(r.ready);
      })
      .catch(() => {
        if (alive) setIssues(null);
      });
    return () => {
      alive = false;
    };
  }, [companyId, onReady]);

  // Alles in Ordnung (oder noch nicht geladen) -> kein Banner.
  if (!issues || issues.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            ELSTER-Versand noch nicht möglich
          </h3>
          <ul className="mt-2 space-y-2">
            {issues.map((issue) => (
              <li key={issue.code} className="text-sm text-amber-800 dark:text-amber-300/90">
                <span>{issue.message}</span>
                {issue.link && issue.action && (
                  <Link
                    to={issue.link}
                    className="mt-0.5 flex items-center gap-1 text-sm font-medium text-amber-700 underline hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200"
                  >
                    {issue.action}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
